import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'utils/dayjs';

import DatePicker from 'components/commons/DatePicker';
import Icon from 'components/commons/Icon';
import {
  DEFAULT_API_DATE_FORMAT,
  DEFAULT_SOURCE_CURRENCY,
  getDisplayValue,
  navigateCurrencyDatePicker,
  prefetchCurrencyYearArchive,
  parseDate,
  shouldNavigateToCurrencyYearArchive,
} from 'utils';
import {
  getMbArchiveDayPath,
  getMbArchiveRangePath,
  getMbPath,
} from 'utils/mbArchive';

import {
  HeaderBlock,
  BackLink,
  BackIcon,
  HeaderRow,
  TitleWrapper,
  SubTitle,
  Title,
  WrapperDatePicker,
  RangeDatePickerWrapper,
  RangeDash,
} from './styles';

const parseArchiveDateValue = parseDate;

const MbArchiveHeader = () => {
  const history = useHistory();
  const { formatMessage, locale } = useIntl();
  const {
    currency = DEFAULT_SOURCE_CURRENCY,
    date,
    dateFrom,
    dateTo,
  } = useParams();
  const mbArchive = useSelector(state => state.currency?.mbArchive);
  const archiveDate = date || dateFrom || mbArchive?.date;
  const [pickerDate, setPickerDate] = useState(() =>
    parseArchiveDateValue(archiveDate)
  );
  const [rangeFrom, setRangeFrom] = useState(() =>
    parseArchiveDateValue(dateFrom)
  );
  const [rangeTo, setRangeTo] = useState(() => parseArchiveDateValue(dateTo));

  useEffect(() => {
    setPickerDate(parseArchiveDateValue(archiveDate));
  }, [archiveDate]);

  useEffect(() => {
    setRangeFrom(parseArchiveDateValue(dateFrom));
  }, [dateFrom]);

  useEffect(() => {
    setRangeTo(parseArchiveDateValue(dateTo));
  }, [dateTo]);

  const currentCurrency = useSelector(state => {
    const list = state.currency?.currencyList || [];
    return (
      list.find(item => item?.name?.toLowerCase() === currency.toLowerCase()) ||
      null
    );
  });

  const currencyLabel =
    currentCurrency?.nameG || currentCurrency?.name || currency.toUpperCase();

  const formattedTitleDate = useMemo(() => {
    if (!archiveDate) {
      return '';
    }

    return dayjs(archiveDate, DEFAULT_API_DATE_FORMAT, true)
      .locale(locale)
      .format('D MMMM YYYY');
  }, [archiveDate, locale]);

  const pageTitle = useMemo(() => {
    if (mbArchive?.mode === 'range' && mbArchive.dateTo) {
      const from = dayjs(mbArchive.date, DEFAULT_API_DATE_FORMAT, true)
        .locale(locale)
        .format('D MMMM YYYY');
      const to = dayjs(mbArchive.dateTo, DEFAULT_API_DATE_FORMAT, true)
        .locale(locale)
        .format('D MMMM YYYY');

      return formatMessage(
        { id: 'mbArchivePage.pageTitle.range' },
        { currency: currencyLabel, dateFrom: from, dateTo: to }
      );
    }

    return formatMessage(
      { id: 'mbArchivePage.pageTitle.day' },
      { currency: currencyLabel, date: formattedTitleDate }
    );
  }, [mbArchive, formatMessage, currencyLabel, formattedTitleDate, locale]);

  const backTo = getMbPath(currency);

  const onDateAccept = value => {
    navigateCurrencyDatePicker({
      history,
      selectedDate: value,
      currency,
      legacyPath: getMbArchiveDayPath(currency, value),
      locale,
    });
  };

  const onDatePickerOpen = () => {
    prefetchCurrencyYearArchive({
      selectedDate: pickerDate,
      currency,
      locale,
    });
  };

  const onRangeAccept = (from, to, acceptedValue) => {
    const isAfterCutover =
      shouldNavigateToCurrencyYearArchive(from) ||
      shouldNavigateToCurrencyYearArchive(to);

    if (isAfterCutover) {
      let selectedDate = to;

      if (shouldNavigateToCurrencyYearArchive(acceptedValue)) {
        selectedDate = acceptedValue;
      } else if (shouldNavigateToCurrencyYearArchive(from)) {
        selectedDate = from;
      }

      navigateCurrencyDatePicker({
        history,
        selectedDate,
        currency,
        locale,
      });

      return;
    }

    const path = getMbArchiveRangePath(currency, from, to);

    if (path) {
      history.push(path);
    }
  };

  const onRangeFromAccept = value => {
    onRangeAccept(value, rangeTo, value);
  };

  const onRangeToAccept = value => {
    onRangeAccept(rangeFrom, value, value);
  };

  return (
    <HeaderBlock>
      <BackLink to={backTo}>
        <BackIcon>
          <Icon name="vectorStroke" />
        </BackIcon>
        <FormattedMessage id="mbArchivePage.backToMb" />
      </BackLink>
      <HeaderRow>
        <TitleWrapper>
          <SubTitle>
            <FormattedMessage id="mbArchivePage.subTitle" />
          </SubTitle>
          <Title>{pageTitle}</Title>
        </TitleWrapper>

        {dateTo ? (
          <RangeDatePickerWrapper>
            <div>
              <DatePicker
                type="outline"
                value={rangeFrom}
                onChange={setRangeFrom}
                getDisplayValue={getDisplayValue}
                onAccept={onRangeFromAccept}
              />
            </div>
            <RangeDash />
            <div>
              <DatePicker
                type="outline"
                value={rangeTo}
                onChange={setRangeTo}
                getDisplayValue={getDisplayValue}
                onAccept={onRangeToAccept}
              />
            </div>
          </RangeDatePickerWrapper>
        ) : (
          <WrapperDatePicker>
            <DatePicker
              type="outline"
              value={pickerDate}
              onChange={setPickerDate}
              getDisplayValue={getDisplayValue}
              onOpen={onDatePickerOpen}
              onAccept={onDateAccept}
            />
          </WrapperDatePicker>
        )}
      </HeaderRow>
    </HeaderBlock>
  );
};

export default MbArchiveHeader;
